"use client";

import { Carousel } from "antd";
import Header from "@/components/Header";
import { motion } from "framer-motion";
import ServiceCard from "../cards/ServiceCard";
import { ChevronLeft, ChevronRight } from "lucide-react";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const cardVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 12,
    },
  },
};

const CustomArrow = ({
  type,
  onClick,
}: {
  type: "prev" | "next";
  onClick?: () => void;
}) => {
  const Icon = type === "prev" ? ChevronLeft : ChevronRight;
  return (
    <button
      onClick={onClick}
      className={`
        absolute z-10 top-1/2 -translate-y-1/2
        ${type === "prev" ? "-left-12" : "-right-12"}
        bg-white/80 hover:bg-white hover:text-primary-color1 hover:scale-125 hover:shadow-xl 
        dark:bg-gray-600 text-gray-800 dark:text-gray-200
        p-2 rounded-full shadow-md transition-all duration-300
        items-center justify-center
        w-10 h-10
        hidden md:flex
      `}
      aria-label={type === "prev" ? "Previous" : "Next"}
    >
      <Icon className="w-6 h-6" />
    </button>
  );
};

export default function Services({ subServices }: { subServices: ServicesResponse }) {
  return (
    <motion.section
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="flex flex-col w-full items-center mt-16 p-8 px-5 sm:px-10 md:px-16 mx-auto mb-16 md:mb-20"
    >
      <Header
        title={subServices?.section?.title || ''}
        paragraph={subServices?.section?.description || ''}
      />

      <motion.div
        className="w-full max-w-7xl relative"
        transition={{ type: "spring", stiffness: 300 }}
      >
        <Carousel
          autoplay
          arrows
          prevArrow={<CustomArrow type="prev" />}
          nextArrow={<CustomArrow type="next" />}
          dots={{ className: "custom-dots" }}
          className="hover:cursor-pointer [&_.slick-dots]:bottom-[-30px] [&_.slick-dots_li_button]:bg-gray-300 [&_.slick-dots_li.slick-active_button]:bg-primary-color1"
        >
          {subServices?.data?.map((service, index) => (
            <motion.div
              key={service.id}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: index * 0.1 }}
              className="px-2"
            >
              <ServiceCard service={service} index={index} />
            </motion.div>
          ))}
        </Carousel>
      </motion.div>
    </motion.section>
  );
}